import { useState, useCallback, useEffect } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import useIsMobile from '../../hooks/useIsMobile';

const FullscreenButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 10,
  right: 10,
  zIndex: 1000,
  backgroundColor: theme.palette.mode === 'light' ? '#FFFFFF' : theme.palette.background.paper,
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : theme.palette.background.default,
  },
  width: 40,
  height: 40
}));

interface Props {
  containerRef: React.RefObject<HTMLDivElement>;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

const FullscreenMapButton = ({ containerRef, onFullscreenChange }: Props) => {
  const isMobile = useIsMobile();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleFullscreenChange = useCallback(() => {
    const doc = document as any;
    const fullscreenActive = !!(
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement
    );
    setIsFullscreen(fullscreenActive);
    onFullscreenChange?.(fullscreenActive);
  }, [onFullscreenChange]);

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [handleFullscreenChange]);

  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;

    try {
      const doc = document as any;
      const isCurrentlyFullscreen = !!(
        doc.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement
      );

      if (!isCurrentlyFullscreen) {
        const element = containerRef.current as any;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
      } else {
        if (doc.exitFullscreen) {
          await doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        } else if (doc.mozCancelFullScreen) {
          await doc.mozCancelFullScreen();
        } else if (doc.msExitFullscreen) {
          await doc.msExitFullscreen();
        }
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  }, [containerRef]);

  return (
    <Tooltip title={isFullscreen ? 'Вийти з повноекранного режиму' : 'Повноекранний режим'}>
      <FullscreenButton onClick={toggleFullscreen} size="small">
        {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
      </FullscreenButton>
    </Tooltip>
  );
};

export default FullscreenMapButton;
